import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { SWR_CONFIG_FOR_STATIC_DATA } from "../../../constants/swr/swrConfigForStaticData";
import useApiFetcher from "../../../hooks/useApiFetcher";
import generateApiUri from "../../../libraries/utils/generateApiUri";
import Autocomplete from "../Autocomplete";

const PlacesAutocomplete = forwardRef(
  /**
   * @typedef {Object} Props
   * @property {string} name
   * @property {number | number[] | ""} value
   * @property {(params: {target: {name: string, value: string | string[]}}) => void} onChange
   * @property {string[]} [environmentId]
   * @property {string[]} [type]
   * @property {boolean} [isMultiple]
   * @property {() => void} [onHydrationDone]
   * @property {object} [queryParams]
   *
   * @param {Props & import("@chakra-ui/react").BoxProps} props
   */
  function PlacesAutocomplete(props, ref) {
    const {
      name,
      value,
      onChange,
      environmentId = [],
      type = [],
      isMultiple = false,
      onHydrationDone,
      queryParams = {},
      ...otherProps
    } = props;

    const [initialValues] = useState(Array.isArray(value) ? value : [value]);

    const resolveItemValue = useCallback(({ item }) => String(item?.id), []);

    const resolveItemName = useCallback(({ item }) => item?.name, []);

    const getEndpointUrl = useCallback(
      ({ fields }) =>
        generateApiUri({
          id: "@places.viewAny",
          query: {
            ...fields,
            type: type,
            environment_id: environmentId,
            ...queryParams,
          },
        }),
      [environmentId, queryParams, type],
    );

    const apiFetcher = useApiFetcher();

    const { data: response, error } = useSWR(
      generateApiUri({
        id: "@places.viewAny",
        query: {
          only_id: initialValues,
        },
      }),
      apiFetcher,
      { ...SWR_CONFIG_FOR_STATIC_DATA },
    );

    const hydrationMap = useMemo(() => {
      const map = {};
      response?.data?.forEach?.((item) => {
        map[item.id] = item;
      });
      return map;
    }, [response?.data]);

    useEffect(() => {
      onHydrationDone?.();
    }, [hydrationMap, onHydrationDone]);

    return (
      <Autocomplete
        ref={ref}
        name={name}
        value={value}
        onChange={onChange}
        endpointUrl={getEndpointUrl}
        resolveItemValue={resolveItemValue}
        resolveItemName={resolveItemName}
        isMultiple={isMultiple}
        {...otherProps}
        valueItemMap={hydrationMap}
        hydrationDone={Boolean(response) || Boolean(error)}
      />
    );
  },
);

export default PlacesAutocomplete;
